import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../api';

const formatDashboardDateTime = (value, timeZone) => {
  if (!value) return '';

  return new Date(value).toLocaleString('en-IN', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatClock = (timeZone, now) => {
  const parts = new Intl.DateTimeFormat('en-IN', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const values = Object.fromEntries(
    parts.map((part) => [part.type, part.value])
  );

  return `${values.weekday}, ${values.day} ${values.month} ${values.year} ${values.hour}:${values.minute}:${values.second}`;
};


const getUtcDayKey = (unixSeconds) => {
  if (!unixSeconds) return '';

  return new Date(unixSeconds * 1000)
    .toISOString()
    .slice(0, 10);
};

const getDailyResult = (row, dayKey) => {
  if (!row || !Array.isArray(row.daily_data)) {
    return null;
  }

  const bucket = row.daily_data.find(
    (item) => getUtcDayKey(item.start_time) === dayKey
  );

  if (!bucket || !Array.isArray(bucket.results)) {
    return null;
  }

  return bucket.results[0] || null;
};

const getDailyCost = (row, dayKey) => {
  const result = getDailyResult(row, dayKey);

  if (!result || !result.amount) {
    return 0;
  }

  const value = Number(result.amount.value);

  return Number.isFinite(value) ? value : 0;
};

const getDailyRequests = (row, dayKey) => {
  const result = getDailyResult(row, dayKey);

  if (!result) {
    return 0;
  }

  const value = Number(result.num_model_requests);

  return Number.isFinite(value) ? value : 0;
};

const getDailyTokenTotal = (
  row,
  dayKey,
  direction
) => {
  const result = getDailyResult(row, dayKey);

  if (!result) {
    return 0;
  }

  const prefix =
    direction === 'input'
      ? 'input_'
      : 'output_';

  return Object.entries(result).reduce(
    (total, [key, value]) => {
      if (
        !key.startsWith(prefix) ||
        !key.endsWith('_tokens')
      ) {
        return total;
      }

      const number = Number(value);

      return Number.isFinite(number)
        ? total + number
        : total;
    },
    0
  );
};

const formatCardNumber = (value) => {
  if (!Number.isFinite(value)) {
    return '0';
  }

  return new Intl.NumberFormat('en-IN').format(value);
};

const formatCardCost = (value) => {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  return `$${value.toFixed(4)}`;
};

const getLastSevenUtcDays = () => {
  const days = [];
  const now = new Date();

  const yesterdayUtc = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - 1
    )
  );

  for (let offset = 0; offset < 7; offset += 1) {
    const date = new Date(yesterdayUtc);
    date.setUTCDate(
      date.getUTCDate() - offset
    );

    days.push(date);
  }

  return days;
};


const getYesterdayUtcDayKey = () => {
  const now = new Date();

  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - 1
    )
  )
    .toISOString()
    .slice(0, 10);
};

const getYesterdayResults = (row, dayKey) => {
  if (!row || !Array.isArray(row.daily_data)) {
    return [];
  }

  const bucket = row.daily_data.find(
    (item) => getUtcDayKey(item.start_time) === dayKey
  );

  if (!bucket || !Array.isArray(bucket.results)) {
    return [];
  }

  return bucket.results;
};

const getYesterdayMetrics = (row, dayKey) => {
  const results = getYesterdayResults(row, dayKey);

  if (results.length === 0) {
    return [];
  }

  const totals = {};

  results.forEach((result) => {
    Object.entries(result || {}).forEach(([key, value]) => {
      if (
        key === 'object' ||
        key.endsWith('_id') ||
        key === 'api_key_id' ||
        key === 'user_id' ||
        key === 'project_id' ||
        key === 'organization_id' ||
        key === 'start_time' ||
        key === 'end_time'
      ) {
        return;
      }

      if (
        value === null ||
        value === undefined ||
        value === ''
      ) {
        return;
      }

      if (key === 'amount' && typeof value === 'object') {
        const amount = Number(value?.value);

        if (Number.isFinite(amount)) {
          totals.amount = (totals.amount || 0) + amount;
        }

        return;
      }

      if (typeof value !== 'number') {
        return;
      }

      if (!Number.isFinite(value)) {
        return;
      }

      totals[key] = (totals[key] || 0) + value;
    });
  });

  return Object.entries(totals).map(
    ([key, value]) => ({
      key,
      value,
    })
  );
};



const formatCardDate = (date) =>
  date.toLocaleDateString('en-IN', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

export default function OpenAIUsage() {
  const [openAISummary, setOpenAISummary] = useState(null);
  const [openAISummaryError, setOpenAISummaryError] = useState('');
  const [currentTimes, setCurrentTimes] = useState({
    ist: '',
    utc: '',
  });

  const [groupingReport, setGroupingReport] = useState(null);
  const [groupingReportError, setGroupingReportError] = useState('');
  const [groupingReportLoading, setGroupingReportLoading] = useState(false);

  const fetchOpenAIUsageSummary = () => {
    api.getOpenAIUsageSummary()
      .then((result) => {
        if (!result?.success) {
          throw new Error(
            result?.error ||
            'Unable to retrieve OpenAI usage summary.'
          );
        }

        setOpenAISummary(result);
        setOpenAISummaryError('');
      })
      .catch((err) => {
        console.error(
          'Failed to load OpenAI usage summary:',
          err
        );
        setOpenAISummaryError(
          err?.message ||
          'Unable to retrieve OpenAI usage summary.'
        );
      });
  };

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();

      setCurrentTimes({
        ist: formatClock('Asia/Kolkata', now),
        utc: formatClock('UTC', now),
      });
    };

    updateClock();

    const clockInterval = setInterval(updateClock, 1000);

    fetchOpenAIUsageSummary();

    const interval = setInterval(() => {
      fetchOpenAIUsageSummary();
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearInterval(clockInterval);
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadGroupingReport = async () => {
      setGroupingReportLoading(true);
      setGroupingReportError('');

      try {
        const result = await api.getOpenAIUsageGroupings();

        if (!result?.success) {
          throw new Error(
            result?.error || 'Unable to load detailed OpenAI groupings.'
          );
        }

        if (active) {
          setGroupingReport(result);
        }
      } catch (error) {
        if (active) {
          setGroupingReportError(
            error?.message || 'Unable to load detailed OpenAI groupings.'
          );
        }
      } finally {
        if (active) {
          setGroupingReportLoading(false);
        }
      }
    };

    loadGroupingReport();

    return () => {
      active = false;
    };
  }, []);

  return (
    <Layout>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        OpenAI Usage
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide">
            IST Time
          </p>
          <p className="text-sm font-semibold text-gray-800 mt-1">
            {currentTimes.ist || 'Loading...'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide">
            UTC Time
          </p>
          <p className="text-sm font-semibold text-gray-800 mt-1">
            {currentTimes.utc || 'Loading...'}
          </p>
        </div>
      </div>

      {openAISummary && (() => {
        const costRow =
          (openAISummary.rows || []).find(
            (row) =>
              row.key === 'costs' ||
              row.name === 'OpenAI Costs'
          );

        const completionRow =
          (openAISummary.rows || []).find(
            (row) =>
              row.key === 'completions' ||
              row.name === 'Completions'
          );

        const sevenDays = getLastSevenUtcDays();

        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
            {sevenDays.map((date) => {
              const dayKey = date
                .toISOString()
                .slice(0, 10);

              const cost = getDailyCost(
                costRow,
                dayKey
              );

              const requests =
                getDailyRequests(
                  completionRow,
                  dayKey
                );

              const inputTokens =
                getDailyTokenTotal(
                  completionRow,
                  dayKey,
                  'input'
                );

              const outputTokens =
                getDailyTokenTotal(
                  completionRow,
                  dayKey,
                  'output'
                );

              return (
                <div
                  key={dayKey}
                  className="bg-white rounded-xl shadow p-4 min-w-0"
                >
                  <p className="text-xs font-semibold text-gray-500 mb-3">
                    {formatCardDate(date)}
                  </p>

                  <div className="space-y-2">
                    <div>
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide">
                        Cost
                      </p>
                      <p className="text-lg font-semibold text-gray-800 break-words">
                        {formatCardCost(cost)}
                      </p>
                    </div>

                    <div>
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide">
                        Requests
                      </p>
                      <p className="text-lg font-semibold text-gray-800 break-words">
                        {formatCardNumber(requests)}
                      </p>
                    </div>

                    <div>
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide">
                        In
                      </p>
                      <p className="text-lg font-semibold text-gray-800 break-words">
                        {formatCardNumber(inputTokens)}
                      </p>
                    </div>

                    <div>
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide">
                        Out
                      </p>
                      <p className="text-lg font-semibold text-gray-800 break-words">
                        {formatCardNumber(outputTokens)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}


      {openAISummary && (() => {
        const yesterdayKey = getYesterdayUtcDayKey();

        const yesterdayRows = (openAISummary.rows || [])
          .filter(
            (row) =>
              row.key !== 'costs' &&
              row.name !== 'OpenAI Costs' &&
              row.key !== 'completions' &&
              row.name !== 'Completions'
          )
          .map((row) => ({
            row,
            metrics: getYesterdayMetrics(
              row,
              yesterdayKey
            ),
          }))
          .filter(
            (item) => item.metrics.length > 0
          );

        if (yesterdayRows.length === 0) {
          return null;
        }

        const yesterdayDate = new Date(
          `${yesterdayKey}T00:00:00Z`
        );

        const formatMetricLabel = (key) => {
          const labels = {
            num_model_requests: 'Requests',
            input_tokens: 'In',
            output_tokens: 'Out',
            input_cached_tokens: 'In Cache',
            input_cache_write_tokens: 'Cache Write',
            input_cache_write_12h_tokens:
              'Cache Write 12h',
            input_uncached_tokens: 'In Uncached',
            input_text_tokens: 'In Text',
            output_text_tokens: 'Out Text',
            input_cached_text_tokens:
              'In Text Cache',
            input_audio_tokens: 'In Audio',
            input_cached_audio_tokens:
              'In Audio Cache',
            output_audio_tokens: 'Out Audio',
            input_image_tokens: 'In Image',
            input_cached_image_tokens:
              'In Image Cache',
            output_image_tokens: 'Out Image',
            seconds: 'Sec',
            duration_seconds: 'Sec',
            minutes: 'Min',
            duration_minutes: 'Min',
            hours: 'Hr',
            duration_hours: 'Hr',
            quantity: 'Qty',
            amount: 'Cost',
            usage_bytes: 'Usage Bytes',
            storage_bytes: 'Storage Bytes',
            characters: 'Characters',
            sessions: 'Sessions',
            requests: 'Requests',
            calls: 'Calls',
          };

          if (labels[key]) {
            return labels[key];
          }

          return key
            .replace(/^input_/, 'In ')
            .replace(/^output_/, 'Out ')
            .replace(/_tokens$/, '')
            .replace(/_seconds$/, ' Sec')
            .replace(/_minutes$/, ' Min')
            .replace(/_hours$/, ' Hr')
            .replace(/_bytes$/, ' Bytes')
            .replace(/_/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (letter) =>
              letter.toUpperCase()
            );
        };

        const formatMetricValue = (key, value) => {
          if (key === 'amount') {
            return formatCardCost(value);
          }

          return formatCardNumber(value);
        };

        return (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  Yesterday
                </h2>
                <p className="text-xs text-gray-400 mt-1">
                  {formatCardDate(yesterdayDate)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {yesterdayRows.map(
                ({ row, metrics }) => (
                  <div
                    key={row.key}
                    className="bg-white rounded-xl shadow p-4 min-w-0"
                  >
                    <p className="text-sm font-semibold text-gray-800 mb-3 break-words">
                      {row.name}
                    </p>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      {metrics.map(
                        ({ key, value }) => (
                          <div
                            key={key}
                            className="min-w-0"
                          >
                            <p className="text-[11px] text-gray-400 uppercase tracking-wide break-words">
                              {formatMetricLabel(key)}
                            </p>

                            <p className="text-lg font-semibold text-gray-800 break-words">
                              {formatMetricValue(
                                key,
                                value
                              )}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        );
      })()}


      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              OpenAI Usage
            </h2>
            <p className="text-xs text-gray-400 mt-1">
              Usage and spend reported by the OpenAI Admin API
            </p>
          </div>

          {openAISummary?.generated_at && (
            <span className="text-xs text-gray-400">
              Updated{' '}
              {formatDashboardDateTime(
                openAISummary.generated_at,
                'Asia/Kolkata'
              )}{' '}
              IST
            </span>
          )}
        </div>

        {openAISummaryError ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600">
            {openAISummaryError}
          </div>
        ) : !openAISummary ? (
          <div className="bg-white rounded-xl shadow p-5 text-sm text-gray-400">
            Loading OpenAI usage...
          </div>
        ) : (
          <div className="space-y-6">
            {(openAISummary.rows || []).map((row) => {
              const dailyData = Array.isArray(row.daily_data)
                ? row.daily_data
                : [];

              const dailyRows = [];

              dailyData.forEach((bucket) => {
                const date = bucket.start_time
                  ? formatDashboardDateTime(
                      new Date(
                        bucket.start_time * 1000
                      ).toISOString(),
                      'UTC'
                    )
                  : 'Unknown';

                const results = Array.isArray(bucket.results)
                  ? bucket.results
                  : [];

                if (results.length > 0) {
                  results.forEach((result, resultIndex) => {
                    dailyRows.push({
                      key:
                        `${row.key}-${bucket.start_time}-${resultIndex}`,
                      date,
                      data: result,
                    });
                  });
                }
              });

              /*
               * OpenAI endpoint tables
               *
               * Rules:
               * - One table per endpoint
               * - Latest row first
               * - No summary/raw JSON tables
               * - No horizontal scrolling
               * - No inner vertical scrolling
               * - Long values wrap vertically
               * - Empty buckets show "No usage"
               * - Do not expose bucket metadata as usage fields
               */

              const tableRows = [...dailyRows].reverse();

              const isEmptyBucket = (data) =>
                data &&
                data.object === 'bucket';

              const displayValue = (value) => {
                if (
                  value === null ||
                  value === undefined ||
                  value === ''
                ) {
                  return '—';
                }

                if (typeof value === 'object') {
                  return JSON.stringify(value);
                }

                const stringValue = String(value);

                /*
                 * Universal compact display for OpenAI object names.
                 *
                 * Examples:
                 * organization.usage.web_searches.result
                 *   -> web_searches.result
                 *
                 * organization.usage.embeddings.result
                 *   -> embeddings.result
                 *
                 * organization.costs.result
                 *   -> costs.result
                 *
                 * This changes display only. The actual API data remains
                 * completely untouched.
                 */
                if (
                  stringValue.startsWith('organization.usage.')
                ) {
                  return stringValue.slice(
                    'organization.usage.'.length
                  );
                }

                if (
                  stringValue.startsWith('organization.costs.')
                ) {
                  return stringValue.slice(
                    'organization.'.length
                  );
                }

                return stringValue;
              };

              /*
               * Build the actual endpoint result columns from the
               * returned result objects.
               *
               * Bucket-only fields such as start_time/end_time/results
               * are deliberately excluded when the bucket has no usage.
               *
               * For Costs, the nested amount object is flattened because
               * currency and value are actual fields inside amount.
               */
              const getColumns = () => {
                const resultObjects = tableRows
                  .map((item) => item.data || {})
                  .filter((data) => !isEmptyBucket(data));

                if (row.name === 'OpenAI Costs') {
                  return [
                    'currency',
                    'amount',
                    'quantity',
                    'quantity_unit',
                  ];
                }

                const columns = [];

                resultObjects.forEach((data) => {
                  Object.keys(data).forEach((key) => {
                    if (
                      key === 'object' ||
                      columns.includes(key)
                    ) {
                      return;
                    }

                    columns.push(key);
                  });
                });

                /*
                 * Completions:
                 * Remove fields that are not useful for this
                 * dashboard's operational usage view.
                 */
                if (row.name === 'Completions') {
                  const removeColumns = new Set([
                    'project_id',
                    'user_id',
                    'api_key_id',
                    'batch',
                    'api_source',
                  ]);

                  return columns.filter(
                    (column) =>
                      !removeColumns.has(column)
                  );
                }

                return columns;
              };

              const columns = getColumns().filter(
                (column) => column !== 'object'
              );

              const columnLabel = (key) => {
                const labels = {
                  project_id: 'Project',
                  project_name: 'Project',
                  organization_id: 'Org ID',
                  organization_name: 'Org',

                  user_id: 'User',
                  user_email: 'Email',
                  api_key_id: 'API Key',
                  api_source: 'Source',

                  num_model_requests: 'Requests',

                  model: 'Model',
                  service_tier: 'Tier',
                  batch: 'Batch',

                  input_tokens: 'In',
                  output_tokens: 'Out',

                  input_cached_tokens: 'In Cache',
                  input_cache_write_tokens: 'Cache Write',
                  input_cache_write_12h_tokens:
                    'Cache Write 12h',
                  input_uncached_tokens: 'In Uncached',

                  input_text_tokens: 'In Text',
                  output_text_tokens: 'Out Text',

                  input_cached_text_tokens:
                    'In Text Cache',

                  input_audio_tokens: 'In Audio',
                  input_cached_audio_tokens:
                    'In Audio Cache',
                  output_audio_tokens: 'Out Audio',

                  input_image_tokens: 'In Image',
                  input_cached_image_tokens:
                    'In Image Cache',
                  output_image_tokens: 'Out Image',

                  seconds: 'Sec',
                  duration_seconds: 'Sec',
                  minutes: 'Min',
                  duration_minutes: 'Min',
                  hours: 'Hr',
                  duration_hours: 'Hr',

                  quantity: 'Qty',
                  quantity_unit: 'Unit',

                  currency: 'Currency',
                  amount: 'Amount',

                  line_item: 'Line Item',
                };

                if (labels[key]) {
                  return labels[key];
                }

                /*
                 * Universal fallback:
                 *
                 * input_something_tokens
                 *   -> Input Something
                 *
                 * duration_seconds
                 *   -> Sec
                 *
                 * Anything unknown remains readable without
                 * exposing the full snake_case API field name.
                 */
                const compactKey = key
                  .replace(/^input_/, 'In ')
                  .replace(/^output_/, 'Out ')
                  .replace(/_tokens$/, '')
                  .replace(/_seconds$/, ' Sec')
                  .replace(/_minutes$/, ' Min')
                  .replace(/_hours$/, ' Hr')
                  .replace(/_cached_/g, ' Cache ')
                  .replace(/_cache_/g, ' Cache ')
                  .replace(/_write_/g, ' Write ')
                  .replace(/_uncached_/g, ' Uncached ')
                  .replace(/_/g, ' ')
                  .replace(/\s+/g, ' ')
                  .trim();

                return compactKey
                  .replace(/\b\w/g, (letter) =>
                    letter.toUpperCase()
                  );
              };

              const getCellValue = (data, column) => {
                if (row.name === 'OpenAI Costs') {
                  if (column === 'currency') {
                    return data.amount?.currency;
                  }

                  if (column === 'amount') {
                    return data.amount?.value;
                  }
                }

                return data[column];
              };

              return (
                <div
                  key={row.key}
                  className="space-y-3"
                >
                  <div>
                    <h3 className="text-base font-semibold text-gray-800 mb-2">
                      {row.name}
                    </h3>

                    <div className="bg-white rounded-xl shadow overflow-hidden">
                      <table className="w-full table-fixed text-sm">
                        <thead className="bg-gray-50 text-left text-gray-500">
                          <tr>
                            <th
                              className="px-3 py-3 font-medium align-top break-words"
                              style={{ width: '12%' }}
                            >
                              Date
                            </th>

                            {columns.map((column) => (
                              <th
                                key={column}
                                className="px-3 py-3 font-medium align-top break-words"
                              >
                                {columnLabel(column)}
                              </th>
                            ))}
                          </tr>
                        </thead>

                        <tbody>
                          {tableRows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={columns.length + 1}
                                className="px-4 py-6 text-center text-sm text-gray-400"
                              >
                                No daily data returned.
                              </td>
                            </tr>
                          ) : (
                            tableRows.map((item) => {
                              const data = item.data || {};
                              const noUsage = isEmptyBucket(data);

                              if (noUsage) {
                                return (
                                  <tr
                                    key={item.key}
                                    className="border-t border-gray-100 align-top"
                                  >
                                    <td className="px-3 py-3 text-gray-700 align-top break-words">
                                      {item.date}
                                    </td>

                                    <td
                                      colSpan={columns.length}
                                      className="px-3 py-3 text-gray-500 align-top break-words"
                                    >
                                      No usage
                                    </td>
                                  </tr>
                                );
                              }

                              return (
                                <tr
                                  key={item.key}
                                  className="border-t border-gray-100 align-top"
                                >
                                  <td className="px-3 py-3 text-gray-700 align-top break-words">
                                    {item.date}
                                  </td>

                                  {columns.map((column) => (
                                    <td
                                      key={column}
                                      className="px-3 py-3 text-gray-700 align-top break-words"
                                    >
                                      {displayValue(
                                        getCellValue(
                                          data,
                                          column
                                        )
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Independent seven-day detailed grouping report */}
      <section className="mt-10 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-gray-800">
            Detailed OpenAI Usage Groupings — Last 7 Days
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Completed UTC days. Each row identifies its endpoint and grouping
            dimensions. Different groupings can overlap; do not add their
            totals together.
          </p>
        </div>

        {groupingReportLoading && !groupingReport && (
          <div className="bg-white rounded-xl shadow p-5 text-sm text-gray-500">
            Loading detailed grouping report...
          </div>
        )}

        {groupingReportError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 break-words">
            {groupingReportError}
          </div>
        )}

        {groupingReport && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-xl shadow p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Grouping requests
                </p>
                <p className="text-xl font-semibold text-gray-800 mt-1">
                  {Number(
                    groupingReport.summary?.grouping_requests || 0
                  ).toLocaleString('en-IN')}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Returned rows
                </p>
                <p className="text-xl font-semibold text-gray-800 mt-1">
                  {Number(
                    groupingReport.summary?.successful_rows || 0
                  ).toLocaleString('en-IN')}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Failed groupings
                </p>
                <p className="text-xl font-semibold text-gray-800 mt-1">
                  {Number(
                    groupingReport.summary?.failed_groupings || 0
                  ).toLocaleString('en-IN')}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="font-semibold text-gray-800">
                  Daily grouping results
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {groupingReport.period?.start_date} to{' '}
                  {groupingReport.period?.end_date_exclusive} UTC
                  (end date exclusive)
                </p>
              </div>

              {(!Array.isArray(groupingReport.rows) ||
                groupingReport.rows.length === 0) ? (
                <div className="p-5 text-sm text-gray-500">
                  No grouping rows were returned for this period.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-600">
                      <tr>
                        <th className="px-3 py-3 font-medium align-top">Date (UTC)</th>
                        <th className="px-3 py-3 font-medium align-top">Endpoint</th>
                        <th className="px-3 py-3 font-medium align-top">Grouped by</th>
                        <th className="px-3 py-3 font-medium align-top">Group values</th>
                        <th className="px-3 py-3 font-medium align-top">Returned metrics</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupingReport.rows.map((item, index) => {
                        const groupBy = Array.isArray(item.group_by)
                          ? item.group_by
                          : [];

                        const groupValues =
                          item.group_values &&
                          typeof item.group_values === 'object'
                            ? item.group_values
                            : {};

                        const metrics =
                          item.metrics &&
                          typeof item.metrics === 'object'
                            ? item.metrics
                            : {};

                        return (
                          <tr
                            key={`${item.date}-${item.endpoint}-${groupBy.join(',')}-${index}`}
                            className="border-t border-gray-100 align-top"
                          >
                            <td className="px-3 py-3 whitespace-nowrap text-gray-800">
                              {item.date || '—'}
                            </td>
                            <td className="px-3 py-3 break-words text-gray-800">
                              {item.endpoint || '—'}
                            </td>
                            <td className="px-3 py-3 break-words">
                              {groupBy.length
                                ? groupBy.join(' + ')
                                : 'Unspecified'}
                            </td>
                            <td className="px-3 py-3 break-words">
                              {groupBy.length ? (
                                <div className="space-y-1">
                                  {groupBy.map((dimension) => (
                                    <div key={dimension}>
                                      <span className="text-gray-500">
                                        {dimension}:{' '}
                                      </span>
                                      <span className="text-gray-800">
                                        {groupValues[dimension] === null ||
                                        groupValues[dimension] === undefined ||
                                        groupValues[dimension] === ''
                                          ? '—'
                                          : String(groupValues[dimension])}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : '—'}
                            </td>
                            <td className="px-3 py-3 break-words min-w-[260px]">
                              <div className="space-y-1">
                                {Object.entries(metrics)
                                  .filter(([key]) => key !== 'object')
                                  .map(([key, value]) => (
                                    <div key={key}>
                                      <span className="text-gray-500">
                                        {key}:{' '}
                                      </span>
                                      <span className="text-gray-800">
                                        {value === null || value === undefined
                                          ? '—'
                                          : typeof value === 'object'
                                            ? JSON.stringify(value)
                                            : String(value)}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {Array.isArray(groupingReport.errors) &&
              groupingReport.errors.length > 0 && (
                <div className="bg-white rounded-xl shadow overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-800">
                      Grouping requests that failed
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      These are API errors, not zero-usage results.
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-gray-600">
                        <tr>
                          <th className="px-3 py-3 font-medium">Endpoint</th>
                          <th className="px-3 py-3 font-medium">Requested grouping</th>
                          <th className="px-3 py-3 font-medium">HTTP status</th>
                          <th className="px-3 py-3 font-medium">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupingReport.errors.map((item, index) => (
                          <tr
                            key={`${item.endpoint}-${index}`}
                            className="border-t border-gray-100 align-top"
                          >
                            <td className="px-3 py-3 break-words">
                              {item.endpoint || '—'}
                            </td>
                            <td className="px-3 py-3 break-words">
                              {Array.isArray(item.group_by)
                                ? item.group_by.join(' + ')
                                : '—'}
                            </td>
                            <td className="px-3 py-3">
                              {item.http_status ?? '—'}
                            </td>
                            <td className="px-3 py-3 break-words text-red-700">
                              {typeof item.error === 'string'
                                ? item.error
                                : JSON.stringify(item.error ?? 'Unknown error')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
          </>
        )}
      </section>

    </Layout>
  );
}
